import { useCallback } from 'react';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useCurrentSiteId } from '@hotjar/site';
import { SurveyTemplatesView } from '@hotjar/survey-templates';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { useWindowTitleWithBlur } from '@hotjar/webapp/hooks/useWindowTitle';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

interface SurveyTemplateSearchedProps {
  resultReturned: boolean;
  textTyped?: string;
}

export const SurveyTemplatesViewContainer = (): JSX.Element | null => {
  const currentSiteId = useCurrentSiteId();
  const hasSurveyCreatePermission = usePermission('surveys-create');
  const { surveyTemplateSearched } = useTrack();

  useWindowTitleWithBlur('Survey Templates', 'Survey Templates');

  const trackQuery = useCallback(
    (totalResults: number, searchText: string) => {
      let params: SurveyTemplateSearchedProps = { resultReturned: true };

      if (totalResults === 0) {
        params = {
          resultReturned: false,
          textTyped: searchText,
        };
      }

      surveyTemplateSearched(params);
    },
    [surveyTemplateSearched],
  );

  if (currentSiteId === null) return null;

  // Check if the user got here from within IWA or navigated manually
  // in order to keep the previous IWA previous page or default responses page
  const handleClose = () => {
    if (browserHistory.action === 'PUSH') {
      browserHistory.back();
    } else {
      navigateTo(SURVEYS.RESPONSES.path, { siteId: currentSiteId });
    }
  };

  return (
    <SurveyTemplatesView
      hasSurveyCreatePermission={hasSurveyCreatePermission}
      onClose={handleClose}
      onContinue={(templateId, categoryTitle) =>
        navigateTo(SURVEYS.CREATE.path, {
          siteId: currentSiteId,
          templateId: templateId ?? '',
          activeFlowSection: 'name',
          category: encodeURIComponent(categoryTitle ?? ''),
        })
      }
      trackQuery={trackQuery}
    />
  );
};
